import { Fragment, FunctionComponent, useState } from 'react';
import classNames from 'clsx';
import { NavIcon } from '../Navbar/NavIcon';
import { NavbarItem } from '../Navbar';
import { RailBrand, RailNavItem, RailProfile, RailTooltip } from './components';
import { useRailCollapsed } from './useRailCollapsed';
import { FunctionSearchTrigger } from '@/features/function-search';

export type RailCustomNode = {
  customKey: string;
  render: (collapsed: boolean) => React.ReactNode;
};

type RailSection = {
  label: string;
  items: (NavbarItem | RailCustomNode)[];
  /** When true, hide the section label and render a horizontal separator in its place. */
  hideLabel?: boolean;
  fixed?: boolean;
};

// Shrink to the viewport height minus the global banner (if any) so the rail
// fits its slot at the top of the page instead of overflowing below the fold
// — which previously forced a "double scroll" to reach the menu footer.
// `--app-banner-h` is published by GlobalBanner; it falls back to 0px when no
// banner is shown. The extra 24px are the 12px the floating rail keeps from the
// top and bottom edges.
export const RAIL_ASIDE_STYLE: React.CSSProperties = {
  height: 'calc(100vh - var(--app-banner-h, 0px) - 24px)'
};

// Compartido con RailSkeleton: el rail provisional del arranque tiene que medir
// exactamente lo mismo que el real para que el cambio no corra el contenido.
// Flota: separado 12px de los bordes (`top-3 my-3 ml-3` + los 24px que descuenta el
// alto), radio 16, vidrio y una sola sombra suave — sin borde. Contraído ocupa lo
// mismo que antes (60 + 12 = 72px).
export const railAsideClassName = (collapsed: boolean, animate = false): string =>
  classNames(
    'group/rail sticky top-3 z-rail my-3 ml-3 hidden shrink-0 flex-col rounded-2xl lg:flex',
    'bg-linear-to-b from-[rgba(var(--f-card-rgb),0.9)] to-[rgba(var(--f-card-rgb),0.76)] backdrop-blur-frost',
    'shadow-[0_1px_2px_rgba(var(--f-ink-rgb),0.06),0_22px_44px_-30px_rgba(var(--f-ink-rgb),0.32),inset_0_1px_0_rgba(var(--f-card-rgb),1)]',
    animate &&
      'transition-[width,padding] [transition-duration:260ms] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
    collapsed ? 'w-[60px] px-2 pt-4 pb-2.5' : 'w-[264px] px-3 pt-[18px] pb-3'
  );

const isCustomNode = (item: NavbarItem | RailCustomNode): item is RailCustomNode => 'customKey' in item;

type RailProps = {
  onHome: string;
  avatar?: string;
  name: string;
  planLabel: string;
  /** El bloque de perfil lleva a Ajustes generales. */
  profileLink?: { href: string; active: boolean };
  sections: RailSection[];
  onLogout?: () => void;
  /**
   * Botones chicos de sistema (campana, Centro de ayuda). Expandido van a la derecha del
   * logo, como la barra del celular; contraído, en columna debajo del buscador.
   */
  headerActions?: (collapsed: boolean) => React.ReactNode;
};

export const Rail: FunctionComponent<RailProps> = ({
  onHome,
  avatar,
  name,
  planLabel,
  profileLink,
  sections,
  onLogout,
  headerActions
}) => {
  const { collapsed, toggle, expand, forced, animate } = useRailCollapsed();

  // Single-open dropdown coordination: only one dropdown stays expanded at a time.
  // Auto-open the parent of the active route on navigation — derived during render,
  // synced into state via the React-blessed `if (a !== b) setState` pattern.
  const autoOpenKey =
    sections
      .flatMap((s) => s.items)
      .find(
        (item): item is NavbarItem =>
          !isCustomNode(item) && !!item.isDropdown && !!item.dropdownContent?.some((c) => c.isSelected)
      )?.label ?? null;
  const [openKey, setOpenKey] = useState<string | null>(autoOpenKey);
  const [prevAuto, setPrevAuto] = useState<string | null>(autoOpenKey);
  if (autoOpenKey !== prevAuto) {
    setPrevAuto(autoOpenKey);
    setOpenKey(autoOpenKey);
  }

  const renderItem = (item: NavbarItem | RailCustomNode) =>
    isCustomNode(item) ? (
      <Fragment key={item.customKey}>{item.render(collapsed)}</Fragment>
    ) : (
      <RailNavItem
        key={item.label}
        item={item}
        collapsed={collapsed}
        onRequestExpand={expand}
        forced={forced}
        isOpen={openKey === item.label}
        onOpenChange={setOpenKey}
      />
    );

  return (
    <aside data-app-rail style={RAIL_ASIDE_STYLE} className={railAsideClassName(collapsed, animate)}>
      {headerActions && !collapsed ? (
        <div className="flex items-start justify-between">
          <RailBrand href={onHome} />
          {/* 36px de alto: centrados contra el logo (34px + 2px de aire arriba). */}
          <div className="flex h-9 items-center gap-0.5">{headerActions(false)}</div>
        </div>
      ) : (
        <RailBrand href={onHome} collapsed={collapsed} />
      )}
      <FunctionSearchTrigger variant="rail" collapsed={collapsed} />
      {headerActions && collapsed && (
        <div className="mt-1 flex flex-col items-center gap-0.5">{headerActions(true)}</div>
      )}

      {/* Toggle — floats on the right edge of the rail. Only hidden on a hard-forced
          route (FORCE_COLLAPSE_PATTERNS, currently none); routes that merely default
          to collapsed (agenda, mi-pagina, the asset editors) keep it so the user can
          still open the rail. */}
      {!forced && (
        // Contraído o no, el nombre sale en el mismo globo oscuro que el resto del riel, no
        // en el `title` nativo. El envoltorio del globo es el que flota en el borde derecho
        // para que el globo se ubique contra el botón.
        <RailTooltip
          label={collapsed ? 'Expandir' : 'Colapsar'}
          enabled
          className="absolute top-[62px] -right-[13px] z-50"
        >
          <button
            type="button"
            onClick={toggle}
            aria-label={collapsed ? 'Expandir barra lateral' : 'Colapsar barra lateral'}
            className={classNames(
              // Sin borde: flota con su sombra, como el riel.
              'grid h-[26px] w-[26px] place-items-center rounded-full',
              'bg-frost-card text-frost-brand',
              'shadow-[0_1px_2px_rgba(var(--f-ink-rgb),0.06),0_6px_14px_-6px_rgba(var(--f-ink-rgb),0.3)]',
              'transition-[opacity,transform,color,box-shadow] duration-200 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
              'hover:text-frost-brand-deep',
              'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-frost-brand/30 focus-visible:ring-offset-2 focus-visible:opacity-100',
              collapsed ? 'opacity-100' : 'opacity-0 group-hover/rail:opacity-100'
            )}
          >
            <NavIcon name={collapsed ? 'chevron-right' : 'chevron-left'} className="text-fs-9" />
          </button>
        </RailTooltip>
      )}

      <div className="scrollbar-auto-hide frost-scroll-fade mt-2.5 flex min-h-0 flex-1 flex-col overflow-y-auto">
        {sections
          .filter((s) => s.items.length > 0 && !s.fixed)
          .map((section, index) => (
            // Cada grupo: 6px arriba y abajo; desde el segundo, una línea fina arriba. Si el
            // grupo tiene nombre (y el riel está expandido), el nombre va debajo de la línea.
            <nav key={section.label} className="relative flex flex-col py-1.5" aria-label={section.label}>
              {index > 0 && (
                <span
                  className={classNames(
                    'absolute top-0 h-px bg-frost-hair-strong',
                    collapsed ? 'inset-x-2' : 'inset-x-2.5'
                  )}
                  aria-hidden
                />
              )}
              {!section.hideLabel && !collapsed && (
                <span className="px-2.5 pt-3.5 pb-1.5 text-fs-11 font-semibold uppercase tracking-[0.12em] whitespace-nowrap text-frost-ink">
                  {section.label}
                </span>
              )}
              {section.items.map(renderItem)}
            </nav>
          ))}
      </div>

      <div className="scrollbar-auto-hide max-h-[35%] shrink-0 overflow-y-auto border-t border-frost-hair-strong pt-1.5">
        {sections
          .filter((section) => section.fixed && section.items.length)
          .map((section) => (
            <nav key={section.label} aria-label={section.label}>
              {section.items.map(renderItem)}
            </nav>
          ))}
      </div>
      <RailProfile
        avatar={avatar}
        name={name}
        planLabel={planLabel}
        link={profileLink}
        collapsed={collapsed}
        onLogout={onLogout}
      />
    </aside>
  );
};
